import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, FolderKanban, Archive, Send, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useLanguage } from '../../context/LanguageContext';

interface KPIMetrics {
  totalRequesters: number;
  totalProviders: number;
  activeProjects: number;
  closedProjects: number;
  archivedProjects: number;
  activeProposals: number;
  acceptedProposals: number;
  rejectedProposals: number;
}

const AdminKPI = () => {
  const { userData } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<KPIMetrics>({
    totalRequesters: 0,
    totalProviders: 0,
    activeProjects: 0,
    closedProjects: 0,
    archivedProjects: 0,
    activeProposals: 0,
    acceptedProposals: 0,
    rejectedProposals: 0
  });

  useEffect(() => {
    if (userData?.role !== 'admin') {
      navigate('/');
      return;
    }

    const fetchMetrics = async () => {
      try {
        setLoading(true);

        // Users metrics
        const usersRef = collection(db, 'users');
        const requesterQuery = query(usersRef, where('role', '==', 'requester'));
        const providerQuery = query(usersRef, where('role', '==', 'provider'));

        const [requesterCount, providerCount] = await Promise.all([
          getCountFromServer(requesterQuery),
          getCountFromServer(providerQuery)
        ]);

        // Projects metrics
        const projectsRef = collection(db, 'projects');
        const activeProjectsQuery = query(projectsRef, where('status', '==', 'active'));
        const closedProjectsQuery = query(projectsRef, where('status', '==', 'closed'));
        const archivedProjectsQuery = query(projectsRef, where('status', '==', 'archived'));

        const [activeProjectsCount, closedProjectsCount, archivedProjectsCount] = await Promise.all([
          getCountFromServer(activeProjectsQuery),
          getCountFromServer(closedProjectsQuery),
          getCountFromServer(archivedProjectsQuery)
        ]);

        // Proposals metrics
        const proposalsRef = collection(db, 'proposals');
        const activeProposalsQuery = query(proposalsRef, where('status', '==', 'pending'));
        const acceptedProposalsQuery = query(proposalsRef, where('status', '==', 'accepted'));
        const rejectedProposalsQuery = query(proposalsRef, where('status', '==', 'rejected'));

        const [activeProposalsCount, acceptedProposalsCount, rejectedProposalsCount] = await Promise.all([
          getCountFromServer(activeProposalsQuery),
          getCountFromServer(acceptedProposalsQuery),
          getCountFromServer(rejectedProposalsQuery)
        ]);

        setMetrics({
          totalRequesters: requesterCount.data().count,
          totalProviders: providerCount.data().count,
          activeProjects: activeProjectsCount.data().count,
          closedProjects: closedProjectsCount.data().count,
          archivedProjects: archivedProjectsCount.data().count,
          activeProposals: activeProposalsCount.data().count,
          acceptedProposals: acceptedProposalsCount.data().count,
          rejectedProposals: rejectedProposalsCount.data().count
        });
      } catch (error) {
        console.error('Error fetching KPI metrics:', error);
        toast.error(t('admin.errorLoadingMetrics'));
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [userData, navigate, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-8">{t('admin.kpiDashboard')}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* User Metrics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="h-5 w-5 text-primary-600 mr-2" />
            {t('admin.userMetrics')}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">{t('admin.totalUsers')} {t('admin.requesters')}</p>
              <p className="text-2xl font-bold text-primary-600">{metrics.totalRequesters}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">{t('admin.totalUsers')} {t('admin.providers')}</p>
              <p className="text-2xl font-bold text-primary-600">{metrics.totalProviders}</p>
            </div>
          </div>
        </div>

        {/* Project Metrics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <FolderKanban className="h-5 w-5 text-primary-600 mr-2" />
            {t('admin.projectMetrics')}
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">{t('admin.activeProjects')}</p>
              <p className="text-2xl font-bold text-green-600">{metrics.activeProjects}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">{t('project.closed')}</p>
              <p className="text-2xl font-bold text-red-600">{metrics.closedProjects}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">{t('project.archived')}</p>
              <p className="text-2xl font-bold text-gray-600">{metrics.archivedProjects}</p>
            </div>
          </div>
        </div>

        {/* Proposal Metrics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Send className="h-5 w-5 text-primary-600 mr-2" />
            {t('admin.proposalMetrics')}
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">{t('project.active')}</p>
              <div className="flex items-center">
                <Send className="h-4 w-4 text-yellow-600 mr-1" />
                <p className="text-2xl font-bold text-yellow-600">{metrics.activeProposals}</p>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">{t('proposal.accepted')}</p>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                <p className="text-2xl font-bold text-green-600">{metrics.acceptedProposals}</p>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">{t('proposal.rejected')}</p>
              <div className="flex items-center">
                <XCircle className="h-4 w-4 text-red-600 mr-1" />
                <p className="text-2xl font-bold text-red-600">{metrics.rejectedProposals}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminKPI;